<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useUIStore } from '@/stores/ui.store'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { useAnchor } from '@/composables/useAnchor'

const ui = useUIStore()
const { t } = useI18n()
const auth = useAuthStore()
const router = useRouter()

/* Signing out has to leave the workspace as well as drop the session —
 * the guard only runs on navigation, so without this the user keeps
 * staring at a page they are no longer entitled to. */
async function onSignOut() {
  ui.closePopups()
  await auth.signOut()
  await router.replace({ name: 'sign-in' })
}

/* Pin under the topbar user button. */
const anchorStyle = useAnchor(
  () => ui.accountOpen,
  () => document.querySelector('.topbar .user')
)
</script>

<template>
  <div
    id="tb-account-pop"
    class="tb-pop account-pop"
    :class="{ 'is-open': ui.accountOpen }"
    :style="anchorStyle"
    @click.stop
  >
    <div class="account-pop__user">
      <div class="account-pop__avatar">{{ auth.user.initials }}</div>
      <div>
        <div class="account-pop__name">{{ auth.user.name }}</div>
        <div class="account-pop__role">{{ auth.user.role }} · {{ auth.user.workspace }}</div>
      </div>
    </div>

    <!-- "⌘," and "?" hints stood beside Settings and Help; no key opens
         either, so a hint promised a shortcut that does nothing. -->
    <nav class="account-pop__list" :aria-label="t('account.menuLabel')">
      <RouterLink class="account-pop__item" :to="{ name: 'account' }" @click="ui.closePopups()">
        <i class="fa-regular fa-user"></i>
        {{ t('account.profile') }}
      </RouterLink>
      <RouterLink class="account-pop__item" :to="{ name: 'settings' }" @click="ui.closePopups()">
        <i class="fa-solid fa-gear"></i>
        {{ t('account.settings') }}
      </RouterLink>
      <RouterLink
        class="account-pop__item"
        :to="{ name: 'account', hash: '#sec-password' }"
        @click="ui.closePopups()"
      >
        <i class="fa-solid fa-shield-halved"></i>
        {{ t('account.security') }}
      </RouterLink>
      <RouterLink class="account-pop__item" :to="{ name: 'help' }" @click="ui.closePopups()">
        <i class="fa-regular fa-circle-question"></i>
        {{ t('account.help') }}
      </RouterLink>
      <div class="account-pop__divider"></div>
      <a class="account-pop__item account-pop__item--danger" href="#" @click.prevent="onSignOut()">
        <i class="fa-solid fa-arrow-right-from-bracket"></i>
        {{ t('account.signOut') }}
      </a>
    </nav>
  </div>
</template>
